import { useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { authApi } from '../../api/services';
import { AuthShell, authButton, authInput, authLabel } from '../../components/Auth/AuthShell';
import { useAuth } from '../../contexts/AuthContext';

/** Ask for a reset email, then set a new password with the 6-digit code from it.
 * (The link in the same email opens /reset-password instead.) */
export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await authApi.requestPasswordReset(email.trim());
      setStep('code');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.reset.sendError'));
    } finally {
      setLoading(false);
    }
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) return setError(t('auth.passwordsMismatch'));
    if (password.length < 6) return setError(t('auth.passwordTooShort'));
    setLoading(true);
    try {
      const response = await resetPassword({ email: email.trim(), code: code.trim(), password });
      toast.success(t('auth.reset.done'));
      navigate(response.user.onboarding_completed ? '/dashboard' : '/onboarding');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.reset.invalid'));
    } finally {
      setLoading(false);
    }
  };

  const errorBox = error && <div role="alert" className="mb-4 p-3 bg-status-danger/10 text-status-danger text-sm rounded-lg">{error}</div>;

  return (
    <AuthShell
      title={t('auth.reset.title')}
      subtitle={step === 'email' ? t('auth.reset.subtitle') : undefined}
      footer={<Link to="/login" className="text-brand-primary font-medium hover:underline">{t('auth.reset.backToLogin')}</Link>}
    >
      {step === 'email' ? (
        <form onSubmit={send}>
          <div className="mb-2">
            <label htmlFor="reset-email" className={authLabel}>{t('auth.reset.emailLabel')}</label>
            <input id="reset-email" type="email" dir="ltr" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className={authInput} required />
          </div>
          <p className="mb-6 text-xs text-text-muted">{t('auth.reset.noEmailHint')}</p>
          {errorBox}
          <button type="submit" disabled={loading} className={authButton}>{loading ? t('auth.reset.sending') : t('auth.reset.send')}</button>
        </form>
      ) : (
        <form onSubmit={save}>
          <p className="mb-5 text-sm text-text-secondary [text-wrap:pretty]">{t('auth.reset.sentBody', { email: email.trim() })}</p>
          <div className="mb-4">
            <label htmlFor="reset-code" className={authLabel}>{t('auth.reset.codeLabel')}</label>
            <input id="reset-code" inputMode="numeric" autoComplete="one-time-code" dir="ltr" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/[^\d۰-۹]/g, '').replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d))))} className={`${authInput} text-center text-2xl tracking-widest font-mono`} required />
          </div>
          <div className="mb-4">
            <label htmlFor="reset-password" className={authLabel}>{t('auth.reset.newPassword')}</label>
            <input id="reset-password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} className={authInput} required />
          </div>
          <div className="mb-6">
            <label htmlFor="reset-confirm" className={authLabel}>{t('auth.reset.confirmNew')}</label>
            <input id="reset-confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={authInput} required />
          </div>
          {errorBox}
          <button type="submit" disabled={loading} className={authButton}>{loading ? t('auth.reset.saving') : t('auth.reset.save')}</button>
          <button type="button" onClick={() => { setStep('email'); setCode(''); setError(null); }} className="w-full mt-3 py-2 text-sm text-text-muted hover:text-text-primary">
            {t('auth.reset.resend')}
          </button>
        </form>
      )}
    </AuthShell>
  );
}
